/**
 * <Divider> — a 1px horizontal separator line (atom, L0.1).
 *
 * Separates sections within a card, list, or accordion body. `spacing` controls the
 * vertical margin above and below; `color` is a semantic token (default `border`).
 * Optional `label` renders a mid-text variant: line | label | line (e.g. "ou"). `icon` suit le libellé, en gris : il
 * dit qu'une explication est à portée (le repère d'un historique, que son appelant déplie au toucher).
 * `vertical` dresse le filet dans une rangée (16 px de haut, `spacing` en marge de part et d'autre) : il sépare deux
 * groupes d'une même ligne (le titre d'une activité de ses vues, au centre du live).
 *
 * No hex literals: the line color is read from `theme.colors.*`.
 */
import React from "react"
import { View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { type SemanticColorKey } from "../../theme/tokens/colors"
import { type SpacingKey } from "../../theme/tokens/spacing"
import { Row } from "../containers/Row"
import { Icon, type IconName } from "../icons/Icon"
import { Text } from "../typography/Text"

/** Le filet debout : la hauteur d'une ligne de texte courant. */
const VERTICAL_HEIGHT = 16

export type DividerProps = {
    /** Vertical margin (top & bottom) — spacing token, defaults to `"2"` (8px). */
    spacing?: SpacingKey
    /** Line color token — defaults to `border`. */
    color?: SemanticColorKey
    /** Optional mid-line label (e.g. `"ou"`). When set, renders line | text | line. */
    label?: string
    /** Un glyphe après le libellé (`label` requis). */
    icon?: IconName
    /** Le filet debout, dans une rangée. Défaut `false`. Sans `label`. */
    vertical?: boolean
    testID?: string
}

export function Divider(props: DividerProps) {
    const { spacing = "2", color = "border", label, icon, vertical = false, testID } = props

    if (vertical) return <View testID={testID} style={styles.upright(spacing, color)} />

    if (label == null || label === "") {
        return <View testID={testID} style={styles.line(spacing, color)} />
    }

    return (
        <Row testID={testID} align="center" gap="4" style={styles.labeled(spacing)}>
            <View style={styles.flexLine(color)} />
            <Row gap="1" align="center">
                <Text size="text-sm" color="text-muted">
                    {label}
                </Text>
                {icon != null && <Icon name={icon} size={14} color="text-muted" />}
            </Row>
            <View style={styles.flexLine(color)} />
        </Row>
    )
}

const styles = StyleSheet.create((theme) => ({
    line: (spacing: SpacingKey, color: SemanticColorKey) => ({
        height: 1,
        alignSelf: "stretch" as const,
        marginVertical: theme.spacing[spacing],
        backgroundColor: theme.colors[color],
    }),
    upright: (spacing: SpacingKey, color: SemanticColorKey) => ({
        width: 1,
        height: VERTICAL_HEIGHT,
        marginHorizontal: theme.spacing[spacing],
        backgroundColor: theme.colors[color],
    }),
    labeled: (spacing: SpacingKey) => ({
        alignSelf: "stretch" as const,
        marginVertical: theme.spacing[spacing],
    }),
    flexLine: (color: SemanticColorKey) => ({
        flex: 1,
        height: 1,
        backgroundColor: theme.colors[color],
    }),
}))
